import { Draggable } from '@src/components/Draggable';
import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import {
  createSnapBoundaries,
  type ElementBoundary,
  generateSnapThreshold,
  getSnapResultForTime,
} from '@src/utility/elementSnapping';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import type { DraggableData, DraggableEvent } from 'react-draggable';

/** Distance from right edge (in pixels) at which auto-zoom activates */
const AUTO_ZOOM_EDGE_THRESHOLD = 60;
/** How much to zoom out per frame (multiplier < 1 means zoom out) */
const ZOOM_OUT_FACTOR = 0.985;
/** Minimum scale multiplier relative to defaultTimelineScale */
const MIN_SCALE_MULTIPLIER = 0.2;
/** Duration expansion percentage when at min zoom (3% of current duration) */
const DURATION_EXPANSION_PERCENT = 0.03;

const Main = styled.div`
  position: absolute;
  z-index: 1;
  top: 20px;
`;

const Head = styled.div`
  box-sizing: content-box;
  padding: 4px;
  height: 8px;
  background: rgb(73, 128, 241);
  border-radius: 2px;
  cursor: ew-resize;
`;

const Line = styled.div`
  width: 1px;
  height: 100%;
  background: rgba(255, 255, 255, 0.5);
`;

/** Timeline left padding offset in pixels */
const TIMELINE_LEFT_OFFSET = 14;

/**
 * Gets the timeline container's left edge position and visible width
 */
function getTimelineContext(): {
  left: number;
  width: number;
  scrollLeft: number;
} | null {
  const timelineRight = document.getElementById('timeline-right');
  const timelineScroll = document.getElementById('timeline-scroll');
  if (!timelineRight || !timelineScroll) return null;
  const rect = timelineRight.getBoundingClientRect();
  return {
    left: rect.left,
    width: rect.width,
    scrollLeft: timelineScroll.scrollLeft,
  };
}

/**
 * Converts mouse clientX to timeline time
 */
function clientXToTime(
  clientX: number,
  containerLeft: number,
  timelineScale: number,
  scrollLeft: number = 0,
): number {
  // pixelPosition in viewport + scrollLeft = absolute position on timeline
  const pixelPosition =
    clientX - containerLeft - TIMELINE_LEFT_OFFSET + scrollLeft;
  return Math.max(0, pixelPosition / timelineScale);
}

/**
 * Extracts clientX from a mouse or touch event
 */
function getClientX(e: DraggableEvent): number | undefined {
  return 'clientX' in e ? e.clientX : (e as TouchEvent).touches?.[0]?.clientX;
}

interface EndMarkerProps {
  expandScaleForDuration: (newDuration: number) => void;
}

export const EndMarker: React.FC<EndMarkerProps> = observer(
  ({ expandScaleForDuration }) => {
    const videoCreator = useVideoCreatorStore();
    const { editorStore } = useStore();
    const { timelineScale, duration: storeDuration, state } = videoCreator;

    const [duration, setDuration] = useState(storeDuration);
    const autoZoomRef = useRef<number | null>(null);
    const isDraggingRef = useRef(false);
    // Track mouse position for updating during zoom
    const lastMouseXRef = useRef<number>(0);
    // Captured at drag start - avoids DOM queries during drag
    const containerLeftRef = useRef<number>(0);
    const containerWidthRef = useRef<number>(0);
    // Cached snap boundaries (all element start/end times) - calculated at drag start
    const snapBoundariesRef = useRef<ElementBoundary[]>([]);
    const snapThresholdRef = useRef<number>(0);
    // Flag to scroll to end after duration extension completes
    const pendingScrollToEndRef = useRef(false);
    // Flag to skip handleStop's duration update when extendAndScrollToEnd already handled it
    const didExtendDurationRef = useRef(false);

    useEffect(() => {
      if (storeDuration !== duration) setDuration(storeDuration);

      // If we extended duration and it has now updated, scroll to the end
      if (pendingScrollToEndRef.current) {
        pendingScrollToEndRef.current = false;
        requestAnimationFrame(() => {
          const timelineScroll = document.getElementById('timeline-scroll');
          if (timelineScroll) {
            const maxScrollLeft =
              timelineScroll.scrollWidth - timelineScroll.clientWidth;
            timelineScroll.scrollLeft = maxScrollLeft;
          }
        });
      }
    }, [storeDuration]);

    // Cleanup auto-zoom on unmount
    useEffect(() => {
      return () => {
        if (autoZoomRef.current) {
          cancelAnimationFrame(autoZoomRef.current);
        }
      };
    }, []);

    const stopAutoZoom = useCallback(() => {
      if (autoZoomRef.current) {
        cancelAnimationFrame(autoZoomRef.current);
        autoZoomRef.current = null;
      }
    }, []);

    const startAutoZoom = useCallback(() => {
      if (autoZoomRef.current) return; // Already zooming

      const zoom = () => {
        if (!isDraggingRef.current) {
          autoZoomRef.current = null;
          return;
        }

        const minScale =
          videoCreator.defaultTimelineScale * MIN_SCALE_MULTIPLIER;
        const currentScale = videoCreator.timelineScale;
        const newScale = currentScale * ZOOM_OUT_FACTOR;

        if (newScale >= minScale) {
          // Still have room to zoom out
          runInAction(() => {
            videoCreator.timelineScale = newScale;

            // Recalculate with new scale to keep marker under cursor
            const newTime = clientXToTime(
              lastMouseXRef.current,
              containerLeftRef.current,
              newScale,
            );
            setDuration(newTime);
          });
        } else {
          // Calculate the time from current mouse position
          const currentTime = clientXToTime(
            lastMouseXRef.current,
            containerLeftRef.current,
            currentScale,
          );
          // At minimum zoom - expand the scale range to fit where user is dragging
          const expansionStep = currentTime * DURATION_EXPANSION_PERCENT;
          expandScaleForDuration(currentTime + expansionStep);
        }

        // Continue zooming/expanding while dragging near edge
        autoZoomRef.current = requestAnimationFrame(zoom);
      };

      autoZoomRef.current = requestAnimationFrame(zoom);
    }, [videoCreator, expandScaleForDuration]);

    const handleStart = useCallback(
      (e: DraggableEvent) => {
        isDraggingRef.current = true;
        runInAction(() => {
          videoCreator.isScrubbing = true;
        });

        // Capture initial mouse position for direction detection
        const clientX = getClientX(e);
        if (clientX !== undefined) {
          lastMouseXRef.current = clientX;
        }

        // Capture the container's position/size at drag start - avoids DOM queries during drag
        const timelineContext = getTimelineContext();
        const containerLeft = timelineContext?.left ?? 0;
        const containerWidth = timelineContext?.width ?? 0;
        const scrollLeft = timelineContext?.scrollLeft ?? 0;
        containerLeftRef.current = containerLeft;
        containerWidthRef.current = containerWidth;

        const scrollbarExists = scrollLeft !== 0;

        // Pre-compute snap boundaries for all element start/end times
        const elements = state?.elements ?? [];
        snapBoundariesRef.current = createSnapBoundaries(elements, []);
        snapThresholdRef.current = generateSnapThreshold(
          videoCreator.timelineScale,
        );

        return { containerLeft, scrollbarExists, scrollLeft };
      },
      [videoCreator, state?.elements],
    );

    /**
     * Extends timeline duration when scrollbar is present and user drags to edge.
     * Commits the change; the useEffect watching storeDuration will scroll to end.
     * Note: handleStop will handle cleanup (isDraggingRef, stopAutoZoom, setSnapGuide).
     */
    const extendAndScrollToEnd = useCallback(
      (currentTime: number) => {
        // Extend by 10%
        const newDuration = currentTime * (1 + DURATION_EXPANSION_PERCENT);

        // Mark that we should scroll to end when duration updates
        pendingScrollToEndRef.current = true;
        // Prevent handleStop from overwriting with stale duration
        didExtendDurationRef.current = true;

        // Commit the new duration
        editorStore.editor.commandSetTimelineDuration(newDuration);

        // Update videoCreator.duration directly to trigger useEffect immediately
        // Otherwise creatomate can finalize previous value and make timeline snap back.
        runInAction(() => {
          videoCreator.duration = newDuration;
        });

        // Move playhead to the new end
        videoCreator.setTime(newDuration);
      },
      [editorStore, videoCreator],
    );

    const handleDrag = useCallback(
      (e: DraggableEvent, _data: DraggableData, context: any) => {
        const clientX = getClientX(e);
        if (clientX === undefined) return;

        // Calculate time from absolute mouse position, accounting for scroll
        let time = clientXToTime(
          clientX,
          context.containerLeft,
          timelineScale,
          context.scrollLeft,
        );

        // Check for snapping to any element start/end time
        const snapBoundaries = snapBoundariesRef.current;
        if (snapBoundaries.length > 0) {
          const snapResult = getSnapResultForTime(
            time,
            snapBoundaries,
            snapThresholdRef.current,
          );

          if (
            snapResult.isWithinSnapWarningRange &&
            snapResult.snapGuideTime != null
          ) {
            videoCreator.setSnapGuide(
              true,
              snapResult.snapGuideTime,
              snapResult.boundary ?? undefined,
            );
          } else {
            videoCreator.setSnapGuide(false);
          }

          // Apply snap if within snap range
          if (snapResult.isWithinSnapRange && snapResult.snapTime != null) {
            time = snapResult.snapTime;
          }
        }

        setDuration(time);

        // Check if mouse is near right edge
        const mousePositionInContainer = clientX - containerLeftRef.current;
        const distanceFromRightEdge =
          containerWidthRef.current - mousePositionInContainer;
        const nearRightEdge = distanceFromRightEdge < AUTO_ZOOM_EDGE_THRESHOLD;

        const movesToTheRight = clientX > lastMouseXRef.current;
        lastMouseXRef.current = clientX;

        if (context.scrollbarExists) {
          // Scrollbar present - extend duration and stop drag when at edge
          // Only extend if dragging to the right
          if (nearRightEdge && movesToTheRight) {
            extendAndScrollToEnd(time);
            return false; // Stop the drag
          }
        } else {
          // No scrollbar - use auto-zoom behavior
          // Only zoom if dragging to the right
          if (nearRightEdge && movesToTheRight) {
            startAutoZoom();
          } else {
            stopAutoZoom();
          }
        }
      },
      [
        timelineScale,
        startAutoZoom,
        stopAutoZoom,
        videoCreator,
        extendAndScrollToEnd,
      ],
    );

    const handleStop = useCallback(async () => {
      isDraggingRef.current = false;
      stopAutoZoom();

      // Clear the snap guide
      videoCreator.setSnapGuide(false);

      // Clear isScrubbing - delay when extending to prevent click events from repositioning playhead
      const clearScrubbing = () => {
        runInAction(() => {
          videoCreator.isScrubbing = false;
        });
      };

      if (didExtendDurationRef.current) {
        // Skip duration command if extendAndScrollToEnd already committed it
        didExtendDurationRef.current = false;
        setTimeout(clearScrubbing, 0);
      } else {
        editorStore.editor.commandSetTimelineDuration(duration);
        clearScrubbing();
      }
    }, [duration, editorStore, videoCreator, stopAutoZoom]);

    return (
      <Draggable onStart={handleStart} onDrag={handleDrag} onStop={handleStop}>
        {ref => (
          <Main ref={ref} style={{ left: duration * timelineScale + 8.5 }}>
            <Head>
              <Line />
            </Head>
          </Main>
        )}
      </Draggable>
    );
  },
);
